import { useState, useCallback, useEffect } from "react";
import { useAuth } from "./useAuth";

/**
 * Fetches the usage summary (DAU/WAU/MAU, health score, trends)
 * for the overview widget on the customer detail page.
 *
 * Calls GET /usage/summaries/{accountId}/
 */
export const useUsageSummary = (accountId) => {
  const { apiClient } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!!accountId);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!accountId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`usage/summaries/${accountId}/`);
      const data = response.data;

      // Map backend field names to what UsageMetrics component expects
      setSummary({
        dau: data.dau_current ?? 0,
        wau: data.wau_current ?? 0,
        mau: data.mau_current ?? 0,
        dau_change: data.dau_change_percent
          ? parseFloat(data.dau_change_percent)
          : 0,
        wau_change: data.wau_change_percent
          ? parseFloat(data.wau_change_percent)
          : 0,
        mau_change: data.mau_change_percent
          ? parseFloat(data.mau_change_percent)
          : 0,
        // Trends: the backend stores a string ("up"/"down"/"stable"),
        // but the sparkline chart expects an array of numbers.
        // Pass empty arrays — the widget handles this gracefully.
        dau_trend: [],
        wau_trend: [],
        mau_trend: [],
        health_score: data.usage_health_score,
        last_activity: data.last_active_at,
      });
    } catch (err) {
      console.error("Error fetching usage summary:", err);
      if (err.response?.status === 404) {
        setSummary(null);
      } else if (err.response?.status !== 401) {
        setError("Failed to fetch usage summary");
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient, accountId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const hasData = summary !== null;

  return {
    summary,
    loading,
    error,
    hasData,
    refetch: fetchSummary,
  };
};
